/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

/**
 * Goat Repository
 * Handles all database operations for goats
 */
class GoatRepository {
  /**
   * Get all goats from the database
   * @returns {Promise<Array<import('../../models/goat.model.js').Goat>>}
   */
  async findAll() {
    try {
      const result = await databaseService.query('SELECT * FROM goats ORDER BY created_at DESC');
      
      return this.mapRows(result.values || []);
    } catch (error) {
      console.error('Error fetching goats:', error);
      throw new Error('Failed to fetch goats from database');
    }
  }

  /**
   * Get a single goat by ID
   * @param {number} id - The goat's ID
   * @returns {Promise<import('../../models/goat.model.js').Goat|null>}
   */
  async findById(id) {
    try {
      const result = await databaseService.query('SELECT * FROM goats WHERE id = ?', [id]);
      
      const rows = this.mapRows(result.values || []);
      return rows.length > 0 ? rows[0] : null;
    } catch (error) {
      console.error('Error fetching goat by ID:', error);
      throw new Error('Failed to fetch goat from database');
    }
  }

  /**
   * Create a new goat
   * @param {Omit<import('../../models/goat.model.js').Goat, 'id'|'createdAt'|'updatedAt'>} goat
   * @returns {Promise<number>} The ID of the created goat
   */
  async create(goat) {
    try {
      const query = `
      INSERT INTO goats (name, date_of_birth, place_of_birth, breed, gender, group_id, eartag, notes)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?)
      `;
      
      const result = await databaseService.run(query, [
        goat.name || null,
        goat.dateOfBirth,
        goat.placeOfBirth || null,
        goat.breed,
        goat.gender,
        goat.groupId || null,
        goat.eartag || null, // Now optional
        goat.notes || null
      ]);


      return result.changes?.lastId || 0;
    } catch (error) {
      console.error('Error creating goat:', error);
      throw new Error('Failed to create goat in database');
    }
  }

  /**
   * Update an existing goat
   * @param {number} id - The goat's ID
   * @param {Partial<import('../../models/goat.model.js').Goat>} goat - Goat data to update
   * @returns {Promise<void>}
   */
  async update(id, goat) {
    try {
      // Fetch existing goat to merge with partial updates
      const existing = await this.findById(id);
      if (!existing) {
        throw new Error(`Goat with ID ${id} not found`);
      }

      const query = `
      UPDATE goats 
        SET name = ?, date_of_birth = ?, place_of_birth = ?, breed = ?, gender = ?, group_id = ?, 
            eartag = ?, notes = ?, updated_at = CURRENT_TIMESTAMP
        WHERE id = ?
      `;
      
      await databaseService.run(query, [
        goat.name !== undefined ? (goat.name || null) : existing.name,
        goat.dateOfBirth !== undefined ? goat.dateOfBirth : existing.dateOfBirth,
        goat.placeOfBirth !== undefined ? (goat.placeOfBirth || null) : existing.placeOfBirth,
        goat.breed !== undefined ? goat.breed : existing.breed,
        goat.gender !== undefined ? goat.gender : existing.gender,
        goat.groupId !== undefined ? (goat.groupId || null) : existing.groupId,
        goat.eartag !== undefined ? goat.eartag : existing.eartag,
        goat.notes !== undefined 
          ? (goat.notes || null) 
          : existing.notes,       
        id
      ]);

    } catch (error) {
      console.error('Error updating goat:', error);
      throw new Error('Failed to update goat in database');
    }
  }

  /**
   * Delete a goat and all related records (cascading delete)
   * 
   * On native platforms (SQLite), foreign key constraints handle cascading.
   * On web (Dexie/IndexedDB), we must manually delete child records first.
   * 
   * Deletion order: weights → temperatures → health → dag → famacha → goat
   * 
   * @param {number} id - The goat's ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      // Dynamic imports prevent circular dependencies
      const { weightRepository } = await import('./weight.repository.js');
      const { temperatureRepository } = await import('./temperature.repository.js');
      const { healthRepository } = await import('./health.repository.js');
      const { dagRepository } = await import('./dag.repository.js');
      const { famachaRepository } = await import('./famacha.repository.js');

      // Delete all child records first (prevents orphaned data)
      await Promise.all([
        weightRepository.deleteByGoat(id),
        temperatureRepository.deleteByGoat(id),
        healthRepository.deleteByGoat(id),
        dagRepository.deleteByGoat(id),
        famachaRepository.deleteByGoat(id)
      ]);

      // Finally, delete the goat itself
      await databaseService.run('DELETE FROM goats WHERE id = ?', [id]);
      
      console.log(`Goat ${id} and all related records deleted successfully`);
    } catch (error) {
      console.error('Error deleting goat:', error);
      throw new Error('Failed to delete goat from database');
    }
  }

  /**
   * Get goats by group ID (using foreign key)
   * @param {number} groupId - Group ID
   * @returns {Promise<Array<import('../../models/goat.model.js').Goat>>}
   */
  async findByGroupId(groupId) {
    try {
      const result = await databaseService.query(
        'SELECT * FROM goats WHERE group_id = ? ORDER BY created_at DESC',
        [groupId]
      );
      
      return this.mapRows(result.values || []);
    } catch (error) {
      console.error('Error fetching goats by group ID:', error);
      throw new Error('Failed to fetch goats from database');
    }
  }

  /**
   * Map database rows to goat objects
   * Converts database field names (snake_case) to model field names (camelCase)
   * Examples: date_of_birth → dateOfBirth, group_id → groupId
   * @private
   * @param {Array<any>} rows - Database rows (snake_case fields)
   * @returns {Array<import('../../models/goat.model.js').Goat>} Mapped objects (camelCase fields)
   */
  mapRows(rows) {
    return rows.map(row => ({
      id: row.id,
      name: row.name,
      dateOfBirth: row.date_of_birth,
      placeOfBirth: row.place_of_birth,
      breed: row.breed,
      gender: row.gender,
      eartag: row.eartag || null,
      groupId: row.group_id,
      notes: row.notes || null, 
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }

}

// Export singleton instance
export const goatRepository = new GoatRepository();
